import { GuidanceMetadata } from "@core/domain/models/reviewItemMetadata/guidancMetadata";
import axios from "axios";

type GuidanceResult = {
  id: string;
  title: string;
  fulltext: string;
  reference: string;
  source: string;
  pdfLink: string;
  type: string;
  publicationDate: string;
};

export class GuidanceApiClient {
  private readonly baseUrl = location.protocol + "//" + location.host + "/guidance";

  async search({
    pagination = {
      limit: 5000,
      offset: 0,
    },
    sources,
    query,
    filters,
  }: {
    sources: string[];
    pagination?: { limit: number; offset: number };
    query?: string;
    filters?: { startDate?: string; endDate?: string };
  }): Promise<{ results: GuidanceMetadata[]; totalFound: number }> {
    const { results, totalFound } = await this.findGuidance({
      sources,
      pagination,
      query,
      filters,
    });

    return {
      results: this.mapResultsToGuidance(results),
      totalFound,
    };
  }

  private async findGuidance({
    pagination,
    sources,
    query,
    filters,
  }: {
    sources: string[];
    pagination: { limit: number; offset: number };
    query?: string;
    filters?: { startDate?: string; endDate?: string };
  }): Promise<{ results: GuidanceResult[]; totalFound: number }> {
    try {
      const solrQuery = query
        ? `(title:(${encodeURIComponent(query)})) OR (summary:(${encodeURIComponent(query)})) OR (fulltext:(${encodeURIComponent(query)}))`
        : encodeURIComponent("*:*");
      const sourceFilter = sources.map((a) => `source:"${encodeURIComponent(a)}"`).join(" OR ");
      const fqParts: string[] = [];
      if (sourceFilter) {
        fqParts.push(sourceFilter);
      }
      if (filters?.startDate || filters?.endDate) {
        const from = filters.startDate ? `${filters.startDate}T00:00:00Z` : "*";
        const to = filters.endDate ? `${filters.endDate}T23:59:59Z` : "*";
        fqParts.push(`publicationDate:[${from} TO ${to}]`);
      }
      const fq = fqParts.map((part) => `&fq=${encodeURIComponent(part)}`).join("");

      const solrUrl = `${this.baseUrl}/?q=${solrQuery}${fq}&start=${pagination.offset}&rows=${pagination.limit}&wt=json`;

      const response = await axios.get(solrUrl);

      return {
        results: (response.data.response.docs || []) as GuidanceResult[],
        totalFound: response.data.response.numFound || 0,
      };
    } catch (error) {
      console.error("Error searching documents:", error);
      return {
        results: [],
        totalFound: 0,
      };
    }
  }

  private mapResultsToGuidance(results: GuidanceResult[]): GuidanceMetadata[] {
    if (!results || !Array.isArray(results)) {
      return [];
    }
    return results.map(
      (result) =>
        new GuidanceMetadata({
          reference: result.reference,
          title: result.title[0],
          attachmentLink: result.pdfLink,
          publishYear: result.publicationDate,
          source: result.source,
        }),
    );
  }
}
